// src/components/atomic-crm/tasks/TaskCardList.tsx
import { useState } from "react";
import { endOfToday, endOfTomorrow, endOfWeek, startOfToday } from "date-fns";
import { useListContext, useTranslate } from "ra-core";
import { RecordContextProvider } from "ra-core";
import { TaskCard } from "./TaskCard";
import { Skeleton } from "@/components/ds/ui/skeleton";
import { cn } from "@/lib/utils";

const startOfTodayDate = startOfToday().toISOString();
const endOfTodayDate = endOfToday().toISOString();
const endOfTomorrowDate = endOfTomorrow().toISOString();
const endOfWeekDate = endOfWeek(new Date(), { weekStartsOn: 1 }).toISOString();

type View = "open" | "closed" | "all";
type ClosedFilter = "done" | "cancelled" | null;

const isClosed = (t: any) =>
  t.status === "done" || t.status === "cancelled" || !!t.done_date;

type Bucket = {
  key: string;
  labelKey: string;
  defaultLabel: string;
  test: (task: any) => boolean;
  group: "open" | "closed";
};

const BUCKETS: Bucket[] = [
  {
    key: "overdue",
    labelKey: "crm.dashboard.task_filters.overdue",
    defaultLabel: "Overdue",
    test: (t) => !isClosed(t) && t.due_date && t.due_date < startOfTodayDate,
    group: "open",
  },
  {
    key: "today",
    labelKey: "crm.dashboard.task_filters.today",
    defaultLabel: "Today",
    test: (t) =>
      !isClosed(t) &&
      t.due_date >= startOfTodayDate &&
      t.due_date <= endOfTodayDate,
    group: "open",
  },
  {
    key: "tomorrow",
    labelKey: "crm.dashboard.task_filters.tomorrow",
    defaultLabel: "Tomorrow",
    test: (t) =>
      !isClosed(t) &&
      t.due_date > endOfTodayDate &&
      t.due_date <= endOfTomorrowDate,
    group: "open",
  },
  {
    key: "this_week",
    labelKey: "crm.dashboard.task_filters.this_week",
    defaultLabel: "This week",
    test: (t) =>
      !isClosed(t) &&
      t.due_date > endOfTomorrowDate &&
      t.due_date <= endOfWeekDate,
    group: "open",
  },
  {
    key: "later",
    labelKey: "crm.dashboard.task_filters.later",
    defaultLabel: "Later",
    test: (t) => !isClosed(t) && (!t.due_date || t.due_date > endOfWeekDate),
    group: "open",
  },
  {
    key: "done",
    labelKey: "crm.task.bucket.done",
    defaultLabel: "Done",
    test: (t) => t.status === "done" || (!t.status && !!t.done_date),
    group: "closed",
  },
  {
    key: "cancelled",
    labelKey: "crm.task.bucket.cancelled",
    defaultLabel: "Cancelled",
    test: (t) => t.status === "cancelled",
    group: "closed",
  },
];

export const TaskCardList = () => {
  const { data, isPending } = useListContext<any>();
  const translate = useTranslate();
  const [view, setView] = useState<View>("open");
  const [closedFilter, setClosedFilter] = useState<ClosedFilter>(null);

  if (isPending) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  const tasks = Array.isArray(data) ? data : [];

  // Which buckets to render
  const visibleBuckets = BUCKETS.filter((bucket) => {
    if (view === "all") return true;
    if (view === "open") return bucket.group === "open";
    // view === "closed"
    if (closedFilter) return bucket.key === closedFilter;
    return bucket.group === "closed";
  });

  const grouped = visibleBuckets
    .map((bucket) => {
      const items = tasks.filter(bucket.test);
      // Sort closed buckets by done_date desc (most recent first)
      if (bucket.group === "closed") {
        items.sort(
          (a: any, b: any) =>
            new Date(b.done_date ?? 0).getTime() -
            new Date(a.done_date ?? 0).getTime(),
        );
      }
      return { ...bucket, items };
    })
    .filter((b) => b.items.length > 0);

  const emptyMessage = (() => {
    if (view === "open")
      return translate("crm.task.empty.open", { _: "No open tasks." });
    if (view === "closed") {
      if (closedFilter === "done")
        return translate("crm.task.empty.done", {
          _: "Nothing completed yet.",
        });
      if (closedFilter === "cancelled")
        return translate("crm.task.empty.cancelled", {
          _: "Nothing cancelled yet.",
        });
      return translate("crm.task.empty.closed", {
        _: "Nothing done or cancelled yet.",
      });
    }
    return translate("crm.task.empty", { _: "No tasks yet." });
  })();

  return (
    <div className="space-y-6">
      {/* Primary view toggle */}
      <div className="flex items-center gap-1.5">
        {(["open", "closed", "all"] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setView(key);
              if (key !== "closed") setClosedFilter(null);
            }}
            className={cn(
              "px-3 py-1 text-xs rounded-full border transition-colors",
              view === key
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground",
            )}
          >
            {key === "open" ? "Open" : key === "closed" ? "Closed" : "All"}
          </button>
        ))}
      </div>

      {/* Sub-filter when Closed is active */}
      {view === "closed" && (
        <div className="flex items-center gap-1.5 -mt-4">
          {(["done", "cancelled"] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() =>
                setClosedFilter((cur) => (cur === key ? null : key))
              }
              className={cn(
                "px-2.5 py-0.5 text-[11px] rounded-full border transition-colors",
                closedFilter === key
                  ? "bg-secondary text-secondary-foreground border-secondary"
                  : "bg-muted/30 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground",
              )}
            >
              {key === "done" ? "Done" : "Cancelled"}
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {grouped.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        grouped.map((bucket) => (
          <div key={bucket.key}>
            <div className="flex items-center justify-between mb-2 px-1">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {translate(bucket.labelKey, { _: bucket.defaultLabel })}
              </h3>
              <span className="text-xs text-muted-foreground">
                {bucket.items.length}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 items-start">
              {bucket.items.map((task: any) => (
                <RecordContextProvider key={task.id} value={task}>
                  <TaskCard />
                </RecordContextProvider>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};
